import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CmsImage } from '@/components/cms/cms-image'

interface ProjectCardProps {
    project: ProjectSummary
    sizes?: string
    preload?: boolean
    headingLevel?: 'h2' | 'h3'
    className?: string
}

/** Portfolio card: photo, type of work, client and place, link to the project page. */
export const ProjectCard: FC<ProjectCardProps> = ({
    project,
    sizes = '(min-width: 1280px) 395px, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw',
    preload = false,
    headingLevel = 'h3',
    className
}): ReactNode => {
    const Heading = headingLevel

    return (
        <article className={cn('h-full', className)}>
            <Link
                href={`/projekti/${project.slug}`}
                className='group flex h-full flex-col rounded-md outline-none focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:ring-offset-4 focus-visible:ring-offset-background'
            >
                <div className='relative aspect-[3/2] overflow-hidden rounded-md bg-muted'>
                    <CmsImage
                        src={project.cover_image}
                        alt={project.cover_image_alt}
                        sizes={sizes}
                        preload={preload}
                        className='transition-transform duration-700 ease-out group-hover:scale-[1.04] motion-reduce:transition-none motion-reduce:group-hover:scale-100'
                    />
                </div>

                <div className='mt-4 flex flex-1 flex-col'>
                    {project.category ? (
                        <p className='text-xs text-muted-foreground' data-mdlfy-cms={`projects:${project.id}:category`}>
                            {project.category}
                        </p>
                    ) : project.service_id && project.service_title && (
                        <p className='text-xs text-muted-foreground' data-mdlfy-cms={`services:${project.service_id}:title`}>
                            {project.service_title}
                        </p>
                    )}

                    <Heading className='mt-1 text-base font-bold tracking-tight transition-colors group-hover:text-primary sm:text-lg'>
                        <span data-mdlfy-cms={`projects:${project.id}:title`}>
                            {project.title}
                        </span>

                        {project.location && (
                            <>
                                <span>, </span>
                                <span data-mdlfy-cms={`projects:${project.id}:location`}>
                                    {project.location}
                                </span>
                            </>
                        )}
                    </Heading>

                    <span className='mt-3 inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.12em] text-primary uppercase'>
                        Vidi više
                        <ArrowRight className='size-3.5 transition-transform group-hover:translate-x-1 motion-reduce:transition-none' aria-hidden='true' />
                    </span>
                </div>
            </Link>
        </article>
    )
}
